// useDocumentLock — bir kayda düzenleme kilidi al, periyodik heartbeat, unmount'ta bırak.
//
// Kullanım:
//   const { state, lockedBy, retry } = useDocumentLock('sales_quotes', selectedId, { enabled: isEditing });
//   state: 'idle' | 'acquiring' | 'mine' | 'locked' | 'error'
//   lockedBy: { user_full_name, user_id, expires_at } | null  (state === 'locked' ise)
//
// state === 'mine' iken kullanıcı düzenleyebilir. 'locked' iken read-only göster + uyarı.
//
// Heartbeat: her 60 saniyede /document-locks/heartbeat
// Cleanup: unmount + tarayıcı sekmesi kapatılınca /document-locks/release
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';

// Sayfa-bazlı session ID — sekme kapanınca expired olur ama unique olarak ayırt edilir
const SESSION_ID = (() => {
  try {
    const k = '__erp_lock_session';
    let v = sessionStorage.getItem(k);
    if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem(k, v); }
    return v;
  } catch { return Math.random().toString(36).slice(2); }
})();

export default function useDocumentLock(table, id, { enabled = true, intervalMs = 60000 } = {}) {
  const [state, setState] = useState('idle');
  const [lockedBy, setLockedBy] = useState(null);
  const timerRef = useRef(null);
  const mounted = useRef(true);

  const acquire = useCallback(async () => {
    if (!table || !id) return;
    setState('acquiring');
    try {
      const r = await api.post('/document-locks/acquire', { table, id, session_id: SESSION_ID });
      if (r.data?.success) {
        if (!mounted.current) return;
        setState('mine'); setLockedBy(null);
      }
    } catch (e) {
      if (e.response?.status === 409) {
        if (!mounted.current) return;
        setState('locked'); setLockedBy(e.response.data?.lock || null);
      } else {
        if (!mounted.current) return;
        setState('error');
      }
    }
  }, [table, id]);

  const heartbeat = useCallback(async () => {
    if (!table || !id) return;
    try {
      const r = await api.post('/document-locks/heartbeat', { table, id });
      // Heartbeat 0 satır güncelliyorsa kilit kaybedilmiş, tekrar al
      if (r.data?.success && r.data.updated === 0) acquire();
    } catch (_) {}
  }, [table, id, acquire]);

  const release = useCallback(async () => {
    if (!table || !id) return;
    try { await api.post('/document-locks/release', { table, id }); } catch (_) {}
  }, [table, id]);

  useEffect(() => {
    mounted.current = true;
    if (!enabled || !table || !id) { setState('idle'); setLockedBy(null); return; }
    acquire();
    // Heartbeat döngüsü
    timerRef.current = setInterval(heartbeat, intervalMs);
    // Sayfa kapatılınca beacon ile bırak
    const onUnload = () => {
      try {
        navigator.sendBeacon &&
          navigator.sendBeacon('/api/document-locks/release-mine',
            new Blob([JSON.stringify({ session_id: SESSION_ID })], { type: 'application/json' }));
      } catch (_) {}
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      mounted.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('beforeunload', onUnload);
      release();
    };
  }, [enabled, table, id, intervalMs, acquire, heartbeat, release]);

  return { state, lockedBy, retry: acquire, release };
}
